// Recording a few seconds of speech, and nothing more.
//
// ⚠️ NOT THE WEB SPEECH API. `SpeechRecognition` is free and needs no server,
// but it exists only in Chrome, sends audio to Google whatever the firm decided
// about AI, and transcribes Hinglish badly — it hears "sharma ka GST kar do" as
// English words that sound similar. This records audio and hands it to the
// firm's own configured provider instead, which is the one already holding
// their key and their monthly cap.
//
// ⚠️ THE MIC IS RELEASED EVERY TIME, on every path out including errors. A
// MediaStream track left live keeps the browser's recording indicator on and,
// on Android, a permanent notification in the shade. Somebody who dictated one
// task at 10am and finds their phone says the app is listening at 4pm does not
// use the feature again, and is right not to.

/** Can this browser record at all? */
export const canRecord = () => (
  typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && typeof window !== 'undefined'
  && typeof window.MediaRecorder !== 'undefined'
);

// ⚠️ ORDER MATTERS, AND SO DOES ASKING. Chrome and Android give opus in webm;
// Safari gives mp4 and refuses webm outright. Passing an unsupported mimeType
// to MediaRecorder throws, and passing none makes the browser choose a format
// the transcriber may not accept — so the first supported one wins and the
// server is told which it got.
const PREFERRED = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

const pickMimeType = () => PREFERRED.find(
  (t) => window.MediaRecorder.isTypeSupported?.(t)
) || '';

const EXTENSION = { 'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg' };

/**
 * Start recording. Resolves once the mic is live, with a handle that either
 * `stop()`s into a Blob or `cancel()`s and throws the audio away.
 *
 * ⚠️ CAPPED AT 60 SECONDS. A recorder left running because somebody walked away
 * mid-sentence would upload minutes of a firm's open-plan office to a third
 * party. The cap stops and transcribes what it has, which is the harmless
 * failure; the other one is not recoverable once sent.
 */
export async function startRecording({ maxMs = 60000, onAutoStop } = {}) {
  if (!canRecord()) throw new Error('unsupported');

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      // Speech down a phone line in a room with other people in it. These are
      // the browser's own DSP and cost nothing to ask for.
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  const mimeType = pickMimeType();
  let recorder;
  try {
    recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  } catch (e) {
    stream.getTracks().forEach((t) => t.stop());
    throw e;
  }

  const chunks = [];
  recorder.addEventListener('dataavailable', (e) => {
    if (e.data?.size) chunks.push(e.data);
  });

  const release = () => stream.getTracks().forEach((t) => t.stop());

  const stopped = new Promise((resolve) => {
    recorder.addEventListener('stop', () => {
      release();
      const type = recorder.mimeType || mimeType || 'audio/webm';
      resolve(new Blob(chunks, { type }));
    });
  });

  recorder.start();

  const timer = setTimeout(() => {
    if (recorder.state === 'recording') {
      recorder.stop();
      onAutoStop?.();
    }
  }, maxMs);

  return {
    mimeType: mimeType || 'audio/webm',
    async stop() {
      clearTimeout(timer);
      if (recorder.state === 'recording') recorder.stop();
      return stopped;
    },
    cancel() {
      clearTimeout(timer);
      // ⚠️ RELEASED BEFORE THE STOP EVENT CAN LAND. Cancel means the audio is
      // not wanted; waiting politely for the blob would keep the mic open for
      // the length of that round trip.
      if (recorder.state === 'recording') recorder.stop();
      release();
      chunks.length = 0;
    },
  };
}

/** A filename the server can read an extension from. */
export function filenameFor(mimeType) {
  const base = String(mimeType || '').split(';')[0];
  return `speech.${EXTENSION[base] || 'webm'}`;
}

// What each server `reason` means, in words somebody holding a phone can act
// on. ⚠️ One place, because the same reasons come back from the chat tier and
// two screens explaining them differently is how "it just doesn't work"
// becomes the only description anyone can give of the feature.
const REASONS = {
  disabled: 'Dictation is switched off for your firm — a manager can turn AI on in settings.',
  unconfigured: 'Dictation is not set up on the server yet.',
  kill_switch: 'Dictation is switched off on the server.',
  cap_reached: "This month's AI limit has been used up.",
  rate_limited: 'The AI service is busy — try again in a moment.',
  circuit_open: 'The AI service is not responding — try again shortly.',
  unavailable: 'Could not reach the AI service — type it instead.',
  no_speech: "I couldn't hear anything — try again, a bit closer.",
  empty_audio: 'Nothing was recorded.',
  too_large: 'That recording was too long.',
  unsupported: 'This browser cannot record audio.',
  denied: 'Microphone access was blocked — allow it in your browser settings.',
};

export const explainVoiceReason = (reason) => REASONS[reason] || 'Could not transcribe that — type it instead.';
